import type { ReactNode } from 'react';
import { money, STATUS_LABEL } from '../lib/types';
import type { Order } from '../lib/types';

export interface OrderAction { label: string; kind?: 'primary' | 'ghost' | 'danger'; disabled?: boolean; onClick: () => void; }

export function fmtWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function etaText(o: Order): string | null {
  if (!o.etaAt || ['picked_up', 'delivered', 'cancelled'].includes(o.status)) return null;
  const mins = Math.round((new Date(o.etaAt).getTime() - Date.now()) / 60000);
  if (Number.isNaN(mins)) return null;
  if (mins <= 0) return 'Due now';
  return `About ${mins} min`;
}

export function PaymentBadge({ order: o }: { order: Order }) {
  if (o.paymentMethod === 'online') {
    if (o.paymentStatus === 'paid') return <span className="tag tag-ok">Paid online</span>;
    if (o.paymentStatus === 'refunded') return <span className="tag tag-muted">Refunded</span>;
    if (o.paymentStatus === 'partially_refunded') return <span className="tag tag-muted">Partly refunded</span>;
    return <span className="tag">{o.paymentStatus}</span>;
  }
  return o.paymentStatus === 'paid'
    ? <span className="tag tag-ok">Paid at store</span>
    : <span className="tag tag-warn">Pay at store</span>;
}

export function OrderCard({ order: o, actions = [], highlight, extra, showCustomer }: { order: Order; actions?: OrderAction[]; highlight?: boolean; extra?: ReactNode; /** Owner board: who placed it and how to reach them. */ showCustomer?: boolean }) {
  const eta = etaText(o);
  return (
    <article className={`order-card order-${o.status} ${highlight ? 'order-hi' : ''}`}>
      <header className="order-head">
        <span className={`status status-${o.status}`}>{STATUS_LABEL[o.status] || o.status}</span>
        <span className="muted small">{fmtWhen(o.createdAt)} · {o.fulfillment === 'delivery' ? 'Delivery' : 'Pickup'}</span>
      </header>
      <ul className="order-lines">
        {o.items.map((l, i) => (
          <li key={i}><span>{l.quantity} × {l.item}</span>{l.notes && <span className="muted small"> — {l.notes}</span>}<span className="price">{typeof l.lineCents === 'number' ? money(l.lineCents) : ''}</span></li>
        ))}
      </ul>
      {showCustomer && (o.customerName || o.customerPhone || o.customerEmail) && (
        <p className="small order-customer">
          <strong>{o.customerName || 'Customer'}</strong>
          {o.customerPhone && <> · <a href={`tel:${o.customerPhone}`}>{o.customerPhone}</a></>}
          {o.customerEmail && <> · <a href={`mailto:${o.customerEmail}`}>{o.customerEmail}</a></>}
        </p>
      )}
      {o.deliveryAddress && <p className="muted small">Deliver to: {o.deliveryAddress}</p>}
      {o.cancelReason && <p className="muted small">Reason: {o.cancelReason}</p>}
      <footer className="order-foot">
        <div className="row wrap" style={{ gap: 8 }}>
          <PaymentBadge order={o} />
          {eta && <span className="tag tag-info">{eta}</span>}
        </div>
        <span className="price big">{money(o.totalCents, o.currency)}</span>
      </footer>
      {extra}
      {actions.length > 0 && (
        <div className="row wrap order-actions">
          {actions.map((a) => (
            <button key={a.label} className={`btn btn-sm ${a.kind === 'ghost' ? 'btn-ghost' : a.kind === 'danger' ? 'btn-danger' : ''}`} disabled={a.disabled} onClick={a.onClick}>{a.label}</button>
          ))}
        </div>
      )}
    </article>
  );
}
